import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { gsap } from 'gsap';
import { BREAKPOINTS } from '../story-config.js';

/**
 * The Pullover Rig — the single visual protagonist of the whole site.
 *
 * Loads the real, 208-piece separated-mesh glTF
 * (`/models/sweater-separated-fixed.glb`) once, and renders it as a pure
 * function of one number: the site-wide story progress `p` (0..1), driven
 * by the single central GSAP timeline in main.js. Nothing here animates on
 * its own — no autoplay, no rAF-time-based motion for the garment or camera.
 *
 * ---------------------------------------------------------------------
 * TUNING:
 * ---------------------------------------------------------------------
 *  - Modellgröße (Anteil der Viewport-Höhe): TARGET_SCREEN_FRACTION (per Tier)
 *  - Kameraposition je Bauphase: CAMERA_KEYFRAMES
 *  - Streuung der Teile bei Progress 0 (Explosionsdistanz): FLOAT_SPREAD (per Tier)
 *  - Bauphasen-Fenster (wann was passiert): BREAKPOINTS in ../story-config.js
 * ---------------------------------------------------------------------
 */

const MODEL_URL = '/models/sweater-separated-fixed.glb';
const TARGET_HEIGHT = 3.4; // world units the whole model is normalised to

// Responsive tiers — desktop: full spatial depth; tablet: reduced float
// distance + fewer particles; mobile: smaller on-screen size so sleeves and
// collar stay fully visible, minimal float distance.
// Explosion distance is deliberately BIG — the pieces genuinely blow apart
// at progress 0, not just hover near their final spot.
const TIERS = {
  desktop: { screenFraction: 0.8, floatSpread: 1.55, particles: 110, maxPixelRatio: 2 },
  tablet: { screenFraction: 0.74, floatSpread: 1.1, particles: 60, maxPixelRatio: 2 },
  mobile: { screenFraction: 0.66, floatSpread: 0.72, particles: 30, maxPixelRatio: 1.5 },
};

function getTier() {
  const w = window.innerWidth;
  if (w <= 640) return 'mobile';
  if (w <= 1024) return 'tablet';
  return 'desktop';
}

const PARALLAX_AMPLITUDE = { x: 0.16, y: 0.1 }; // finale mouse-parallax amplitude
const SETTLE_ROTATION = 0.22; // radians of yaw settle during the finale
const SPIN_TURNS = 1.6; // extra full-ish rotations pieces tumble through while exploded

// Camera keyframes, one per chapter boundary — interpolated smoothly by `p`.
// pos/look in world units, fov in degrees. Bigger swings + a touch of roll
// for a more dynamic, kinetic camera language. Edit here to change camera work.
const CAMERA_KEYFRAMES = [
  { p: 0, pos: [0, 0.35, 11.5], look: [0, 0.1, 0], fov: 52, roll: 0.03 },
  { p: BREAKPOINTS.hero_end, pos: [0.3, 0.1, 9.4], look: [0, 0, 0], fov: 45, roll: -0.02 },
  { p: BREAKPOINTS.material_end, pos: [2.1, -0.25, 7.6], look: [0.35, -0.1, 0], fov: 41, roll: 0.035 },
  { p: BREAKPOINTS.design_end, pos: [-1.9, 0.2, 7.1], look: [-0.3, 0.05, 0], fov: 39.5, roll: -0.04 },
  { p: BREAKPOINTS.sleeves_end, pos: [1.1, 0.35, 6.7], look: [0.15, 0.15, 0], fov: 38, roll: 0.025 },
  { p: BREAKPOINTS.final_end, pos: [0, 0.08, 6.85], look: [0, 0, 0], fov: 36.5, roll: 0 },
  { p: 1, pos: [0, 0, 6.5], look: [0, 0, 0], fov: 34.5, roll: 0 },
];

const arriveEase = gsap.parseEase('power3.out');
const settleEase = gsap.parseEase('sine.inOut');
const camEase = gsap.parseEase('power2.inOut');

// Compact Ashima/McEwan 3D simplex noise (public-domain style) used for the
// assembly "thread dissolve" edge shimmer.
const SNOISE_GLSL = `
vec3 mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;}
vec4 mod289(vec4 x){return x-floor(x*(1.0/289.0))*289.0;}
vec4 permute(vec4 x){return mod289(((x*34.0)+1.0)*x);}
vec4 taylorInvSqrt(vec4 r){return 1.79284291400159-0.85373472095314*r;}
float snoise(vec3 v){
  const vec2 C=vec2(1.0/6.0,1.0/3.0);
  const vec4 D=vec4(0.0,0.5,1.0,2.0);
  vec3 i=floor(v+dot(v,C.yyy));
  vec3 x0=v-i+dot(i,C.xxx);
  vec3 g=step(x0.yzx,x0.xyz);
  vec3 l=1.0-g;
  vec3 i1=min(g.xyz,l.zxy);
  vec3 i2=max(g.xyz,l.zxy);
  vec3 x1=x0-i1+C.xxx;
  vec3 x2=x0-i2+C.yyy;
  vec3 x3=x0-D.yyy;
  i=mod289(i);
  vec4 p=permute(permute(permute(
    i.z+vec4(0.0,i1.z,i2.z,1.0))
    +i.y+vec4(0.0,i1.y,i2.y,1.0))
    +i.x+vec4(0.0,i1.x,i2.x,1.0));
  float n_=0.142857142857;
  vec3 ns=n_*D.wyz-D.xzx;
  vec4 j=p-49.0*floor(p*ns.z*ns.z);
  vec4 x_=floor(j*ns.z);
  vec4 y_=floor(j-7.0*x_);
  vec4 x=x_*ns.x+ns.yyyy;
  vec4 y=y_*ns.x+ns.yyyy;
  vec4 h=1.0-abs(x)-abs(y);
  vec4 b0=vec4(x.xy,y.xy);
  vec4 b1=vec4(x.zw,y.zw);
  vec4 s0=floor(b0)*2.0+1.0;
  vec4 s1=floor(b1)*2.0+1.0;
  vec4 sh=-step(h,vec4(0.0));
  vec4 a0=b0.xzyw+s0.xzyw*sh.xxyy;
  vec4 a1=b1.xzyw+s1.xzyw*sh.zzww;
  vec3 p0=vec3(a0.xy,h.x);
  vec3 p1=vec3(a0.zw,h.y);
  vec3 p2=vec3(a1.xy,h.z);
  vec3 p3=vec3(a1.zw,h.w);
  vec4 norm=taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));
  p0*=norm.x; p1*=norm.y; p2*=norm.z; p3*=norm.w;
  vec4 m=max(0.6-vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.0);
  m=m*m;
  return 42.0*dot(m*m,vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));
}
`;

const EDGE_COLOR = 0xc9a227; // --gold
const SWEEP_COLOR = 0xfff2d6;

/** Wraps a source PBR material's textures in an assembly-capable MeshStandardMaterial. */
function buildAssemblyMaterial(sourceMaterial, sharedUniforms) {
  const src = sourceMaterial || {};
  const mat = new THREE.MeshStandardMaterial({
    map: src.map || null,
    normalMap: src.normalMap || null,
    roughnessMap: src.roughnessMap || null,
    metalnessMap: src.metalnessMap || null,
    color: src.color ? src.color.clone() : new THREE.Color(0xffffff),
    roughness: src.roughness ?? 1,
    metalness: src.metalness ?? 0,
    side: src.side ?? THREE.FrontSide,
    transparent: true,
    opacity: 0, // faded in once the model has finished loading, see update()
  });
  if (mat.map) mat.map.colorSpace = THREE.SRGBColorSpace;

  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uDissolveProgress = sharedUniforms.uDissolveProgress;
    shader.uniforms.uEdgeColor = sharedUniforms.uEdgeColor;
    shader.uniforms.uEdgeWidth = sharedUniforms.uEdgeWidth;
    shader.uniforms.uNoiseScale = sharedUniforms.uNoiseScale;
    shader.uniforms.uSweepY = sharedUniforms.uSweepY;
    shader.uniforms.uSweepIntensity = sharedUniforms.uSweepIntensity;
    shader.uniforms.uSweepColor = sharedUniforms.uSweepColor;

    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vDissolvePos;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvDissolvePos = position;');

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
        uniform float uDissolveProgress;
        uniform vec3 uEdgeColor;
        uniform float uEdgeWidth;
        uniform float uNoiseScale;
        uniform float uSweepY;
        uniform float uSweepIntensity;
        uniform vec3 uSweepColor;
        varying vec3 vDissolvePos;
        ${SNOISE_GLSL}`
      )
      .replace(
        '#include <clipping_planes_fragment>',
        `#include <clipping_planes_fragment>
        float dissolveNoise = snoise(vDissolvePos * uNoiseScale) * 0.5 + 0.5;
        if (uDissolveProgress > 0.0 && dissolveNoise < uDissolveProgress) discard;`
      )
      .replace(
        '#include <dithering_fragment>',
        `float dissolveEdge = smoothstep(uDissolveProgress, uDissolveProgress + uEdgeWidth, dissolveNoise);
        gl_FragColor.rgb += uEdgeColor * (1.0 - dissolveEdge) * step(0.0001, uDissolveProgress) * 2.0;
        float sweepBand = 1.0 - smoothstep(0.0, 0.45, abs(vDissolvePos.y - uSweepY));
        gl_FragColor.rgb += uSweepColor * sweepBand * uSweepIntensity;
        #include <dithering_fragment>`
      );
  };

  return mat;
}

// Soft radial-gradient sprite for the particles — reads as fine smoke/dust
// rather than hard dots. Generated once, reused across every particle.
let smokeSpriteTexture = null;
function getSmokeSprite() {
  if (smokeSpriteTexture) return smokeSpriteTexture;
  const size = 128;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d');
  const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, 'rgba(255,255,255,0.95)');
  grad.addColorStop(0.35, 'rgba(255,255,255,0.55)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  smokeSpriteTexture = new THREE.CanvasTexture(c);
  return smokeSpriteTexture;
}

// Smoke/dust colour palette — gold, warm bone, faint rosso — for a livelier,
// less monochrome burst than a single flat colour.
const SMOKE_PALETTE = [
  new THREE.Color(0xd8b657),
  new THREE.Color(0xfff2d6),
  new THREE.Color(0xe8431e),
];

function buildParticles(count, radius) {
  const basePositions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  const sizes = new Float32Array(count);
  const directions = [];
  const swirlSeeds = [];
  for (let i = 0; i < count; i++) {
    // Deterministic one-time setup scatter, not per-frame randomness.
    const r = radius * (0.45 + Math.random() * 1.1);
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(Math.random() * 2 - 1);
    const x = r * Math.sin(phi) * Math.cos(theta);
    const y = r * Math.sin(phi) * Math.sin(theta);
    const z = r * Math.cos(phi) * 0.7;
    basePositions[i * 3] = x;
    basePositions[i * 3 + 1] = y;
    basePositions[i * 3 + 2] = z;
    directions.push(new THREE.Vector3(x, y, z).normalize());
    swirlSeeds.push(Math.random() * Math.PI * 2);

    const col = SMOKE_PALETTE[i % SMOKE_PALETTE.length];
    colors[i * 3] = col.r;
    colors[i * 3 + 1] = col.g;
    colors[i * 3 + 2] = col.b;
    sizes[i] = 0.05 + Math.random() * 0.16;
  }

  const geometry = new THREE.BufferGeometry();
  const drawPositions = new Float32Array(basePositions);
  geometry.setAttribute('position', new THREE.BufferAttribute(drawPositions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geometry.setAttribute('pSize', new THREE.BufferAttribute(sizes, 1));

  const material = new THREE.PointsMaterial({
    size: 1,
    map: getSmokeSprite(),
    vertexColors: true,
    sizeAttenuation: true,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  // Per-particle size on top of the shared PointsMaterial base `size` uniform
  // (renamed attribute to `pSize` to avoid clashing with that uniform).
  material.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float pSize;')
      .replace('gl_PointSize = size;', 'gl_PointSize = size * pSize;');
  };

  const points = new THREE.Points(geometry, material);
  points.frustumCulled = false;

  return { points, material, basePositions, drawPositions, directions, swirlSeeds };
}

function lerp3(a, b, t) {
  return [
    THREE.MathUtils.lerp(a[0], b[0], t),
    THREE.MathUtils.lerp(a[1], b[1], t),
    THREE.MathUtils.lerp(a[2], b[2], t),
  ];
}

/** Piecewise-smooth interpolation across CAMERA_KEYFRAMES for progress p. */
function sampleCamera(p) {
  const kfs = CAMERA_KEYFRAMES;
  let seg = kfs.length - 2;
  for (let i = 0; i < kfs.length - 1; i++) {
    if (p >= kfs[i].p && p <= kfs[i + 1].p) {
      seg = i;
      break;
    }
  }
  const a = kfs[seg];
  const b = kfs[seg + 1];
  const span = Math.max(1e-6, b.p - a.p);
  const localT = camEase(THREE.MathUtils.clamp((p - a.p) / span, 0, 1));
  return {
    pos: lerp3(a.pos, b.pos, localT),
    look: lerp3(a.look, b.look, localT),
    fov: THREE.MathUtils.lerp(a.fov, b.fov, localT),
    roll: THREE.MathUtils.lerp(a.roll ?? 0, b.roll ?? 0, localT),
  };
}

/** Small, deterministic (progress-driven, not time-driven) punch-in pulse
 * that fires as `p` crosses each chapter breakpoint — for extra kinetic
 * energy at every hand-off, without ever animating on its own. */
function breakpointPulse(p) {
  const marks = [
    BREAKPOINTS.hero_end,
    BREAKPOINTS.material_end,
    BREAKPOINTS.design_end,
    BREAKPOINTS.sleeves_end,
    BREAKPOINTS.final_end,
  ];
  let pulse = 0;
  const width = 0.045;
  marks.forEach((m) => {
    const d = Math.abs(p - m);
    if (d < width) pulse = Math.max(pulse, 1 - d / width);
  });
  return pulse;
}

export function createSweaterRig({ scene, camera, reducedMotion, onProgressLoad }) {
  let tier = getTier();
  let TIER_CFG = TIERS[tier];

  const group = new THREE.Group();
  scene.add(group);

  const sharedUniforms = {
    uDissolveProgress: { value: 0 },
    uEdgeColor: { value: new THREE.Color(EDGE_COLOR) },
    uEdgeWidth: { value: 0.12 },
    uNoiseScale: { value: 1.7 },
    uSweepY: { value: 10 },
    uSweepIntensity: { value: 0 },
    uSweepColor: { value: new THREE.Color(SWEEP_COLOR) },
  };

  const pieces = []; // { holder, mesh, dir, seed, start, end }
  const materials = [];
  let particleRig = buildParticles(TIER_CFG.particles, TARGET_HEIGHT * 0.5);
  group.add(particleRig.points);

  let loaded = false;
  let loadFailed = false;
  let targetProgress = 0;
  let currentProgress = 0;
  let modelScale = 1;
  const onLoadedCallbacks = [];

  // Finale mouse parallax (only ever active in the 90–100% product-hero
  // chapter — a stationary interaction, not scroll- or time-driven motion
  // of the assembly itself).
  const pointer = { x: 0, y: 0 };
  window.addEventListener('pointermove', (e) => {
    pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
    pointer.y = (e.clientY / window.innerHeight) * 2 - 1;
  });

  function updateModelScale() {
    const vFov = (camera.fov * Math.PI) / 180;
    const frustumHeight = 2 * Math.tan(vFov / 2) * camera.position.z;
    const targetWorldHeight = frustumHeight * TIER_CFG.screenFraction;
    modelScale = targetWorldHeight / TARGET_HEIGHT;
  }
  updateModelScale();

  new GLTFLoader().load(
    MODEL_URL,
    (gltf) => {
      const model = gltf.scene;

      // Centre + normalise scale via the model's OVERALL bounding box only
      // — never per-mesh, never geometry.center()/mesh.position.set(0,0,0).
      // Each of the 208 meshes keeps its authored position/rotation/scale
      // relative to that single, shared centre.
      const box = new THREE.Box3().setFromObject(model);
      const size = new THREE.Vector3();
      box.getSize(size);
      const center = new THREE.Vector3();
      box.getCenter(center);
      model.position.sub(center);
      const normaliseFactor = size.y > 0 ? TARGET_HEIGHT / size.y : 1;
      model.scale.setScalar(normaliseFactor);
      model.updateMatrixWorld(true);

      const meshes = [];
      model.traverse((child) => {
        if (child.isMesh) meshes.push(child);
      });

      if (meshes.length === 0) {
        console.error('[sweaterRig] sweater-separated-fixed.glb contains no mesh nodes.');
        loadFailed = true;
        return;
      }

      const meshInfo = meshes.map((mesh) => {
        const b = new THREE.Box3().setFromObject(mesh);
        const c = new THREE.Vector3();
        b.getCenter(c);
        const s = new THREE.Vector3();
        b.getSize(s);
        return { center: c, volume: s.x * s.y * s.z };
      });
      const overallCenter = meshInfo
        .reduce((acc, m) => acc.add(m.center), new THREE.Vector3())
        .divideScalar(meshInfo.length);

      // Size-based arrival rank: small pieces first, large structural
      // panels (sleeves/body/collar) close last.
      const arrivalOrder = meshInfo
        .map((m, i) => ({ i, volume: m.volume }))
        .sort((a, b) => a.volume - b.volume)
        .map((entry) => entry.i);
      const rankOf = new Array(meshes.length);
      arrivalOrder.forEach((meshIndex, rank) => {
        rankOf[meshIndex] = rank / Math.max(1, meshes.length - 1);
      });

      scene.updateMatrixWorld(true);

      const staggerStart = BREAKPOINTS.hero_end;
      const staggerEnd = BREAKPOINTS.final_end;

      meshes.forEach((mesh, i) => {
        const holder = new THREE.Group();
        group.add(holder);
        holder.updateMatrixWorld(true);
        // attach() preserves the mesh's world transform while re-parenting
        // it into a flat holder — this is what makes each piece
        // independently movable without ever touching the mesh's own
        // authored local transform, which remains the exact original
        // geometry transform for the lifetime of the page.
        holder.attach(mesh);

        // Required by spec: capture the untouched original transform.
        mesh.userData.originalPosition = mesh.position.clone();
        mesh.userData.originalQuaternion = mesh.quaternion.clone();
        mesh.userData.originalScale = mesh.scale.clone();

        let dir = meshInfo[i].center.clone().sub(overallCenter);
        if (dir.lengthSq() < 1e-6) {
          const a = i * 2.399963229728653; // golden-angle fallback, deterministic
          dir.set(Math.cos(a), Math.sin(a) * 0.6, Math.sin(a) * 0.8);
        }
        dir.normalize();

        mesh.castShadow = false;
        mesh.receiveShadow = false;

        const applyDissolve = (mat) => {
          const m = buildAssemblyMaterial(mat, sharedUniforms);
          materials.push(m);
          return m;
        };
        mesh.material = Array.isArray(mesh.material)
          ? mesh.material.map(applyDissolve)
          : applyDissolve(mesh.material);

        const rank = rankOf[i];
        const start = staggerStart + rank * (staggerEnd - staggerStart) * 0.55;
        const end = Math.min(staggerEnd, start + (staggerEnd - staggerStart) * 0.45);

        pieces.push({ holder, mesh, dir, seed: i * 12.9898, start, end });
      });

      loaded = true;
      onLoadedCallbacks.forEach((cb) => cb());
      onLoadedCallbacks.length = 0;
    },
    (xhr) => {
      if (onProgressLoad && xhr.total) onProgressLoad(xhr.loaded / xhr.total);
    },
    (err) => {
      console.error('[sweaterRig] failed to load sweater-separated-fixed.glb', err);
      loadFailed = true;
    }
  );

  function setProgress(p) {
    targetProgress = THREE.MathUtils.clamp(p, 0, 1);
  }

  function resize() {
    tier = getTier();
    TIER_CFG = TIERS[tier];
    updateModelScale();
    const nextCount = TIER_CFG.particles;
    if (nextCount !== particleRig.directions.length) {
      group.remove(particleRig.points);
      particleRig.points.geometry.dispose();
      particleRig.material.dispose();
      particleRig = buildParticles(nextCount, TARGET_HEIGHT * 0.5);
      group.add(particleRig.points);
    }
  }

  function whenLoaded(cb) {
    if (loaded) cb();
    else onLoadedCallbacks.push(cb);
  }

  function update(dt) {
    if (loadFailed) return;

    const follow = reducedMotion ? 1 : Math.min(dt * 6, 1);
    currentProgress += (targetProgress - currentProgress) * follow;
    const p = reducedMotion ? 1 : currentProgress;

    group.scale.setScalar(modelScale);

    const fadeTarget = loaded ? 0.98 : 0;
    materials.forEach((m) => {
      m.opacity += (fadeTarget - m.opacity) * Math.min(dt * 2.5, 1);
    });
    const particleFadeCap = fadeTarget > 0 ? 1 : 0;

    // Deterministic punch-in pulse at every chapter hand-off — purely a
    // function of p, used to spike the camera, the dissolve glow and the
    // particle burst for a few frames of extra energy at each transition.
    const pulse = reducedMotion ? 0 : breakpointPulse(p);

    // ---- Camera: purely a function of p (+ finale mouse parallax) -------
    const cam = sampleCamera(p);
    camera.position.set(cam.pos[0], cam.pos[1], cam.pos[2]);
    if (p >= BREAKPOINTS.final_end && !reducedMotion) {
      const finaleT = THREE.MathUtils.clamp(
        (p - BREAKPOINTS.final_end) / (1 - BREAKPOINTS.final_end),
        0,
        1
      );
      camera.position.x += pointer.x * PARALLAX_AMPLITUDE.x * finaleT;
      camera.position.y += -pointer.y * PARALLAX_AMPLITUDE.y * finaleT;
    }
    // Punch-in: a quick, small FOV kick right at each breakpoint crossing.
    camera.fov = cam.fov - pulse * 1.6;
    camera.updateProjectionMatrix();
    camera.up.set(Math.sin(cam.roll), Math.cos(cam.roll), 0);
    camera.lookAt(cam.look[0], cam.look[1], cam.look[2]);

    if (!loaded) return;

    pieces.forEach(({ holder, dir, seed, start, end }) => {
      const localT = THREE.MathUtils.clamp((p - start) / Math.max(0.0001, end - start), 0, 1);
      const arrived = arriveEase(localT); // 0 = floating, 1 = at true position
      const remaining = 1 - arrived;

      holder.position.set(
        dir.x * TIER_CFG.floatSpread * remaining,
        dir.y * TIER_CFG.floatSpread * remaining,
        dir.z * TIER_CFG.floatSpread * remaining
      );
      // Big, tumbling spin while exploded, unwinding to exactly zero as the
      // piece arrives — deterministic per-piece seed, not per-frame random.
      const spin = remaining * Math.PI * 2 * SPIN_TURNS;
      holder.rotation.set(
        Math.sin(seed) * spin * 0.5 + dir.y * remaining * 0.3,
        Math.cos(seed * 1.7) * spin * 0.5 + Math.sin(seed) * remaining * 0.24,
        Math.sin(seed * 0.6) * spin * 0.35 + dir.x * remaining * 0.26
      );
      // A small scale "punch" — pieces are ever so slightly larger while
      // fully exploded, snapping to their true size (1.0) as they land.
      const s = 1 + remaining * 0.08;
      holder.scale.setScalar(s);
    });

    // Settle: eased rotation + scale flourish once fully arrived, plus a
    // soft light sweep travelling down the garment during the finale.
    const settleT = THREE.MathUtils.clamp(
      (p - BREAKPOINTS.final_end) / (1 - BREAKPOINTS.final_end),
      0,
      1
    );
    const settleEased = settleEase(settleT);
    group.rotation.y = settleEased * SETTLE_ROTATION * Math.sin(settleEased * Math.PI);

    sharedUniforms.uSweepIntensity.value = Math.sin(settleT * Math.PI) * 0.55;
    sharedUniforms.uSweepY.value = THREE.MathUtils.lerp(1.6, -1.6, settleT);

    // Thread-dissolve shimmer across the assembly window, spiking brighter
    // at every chapter breakpoint for a more explosive, electric edge-glow.
    const dissolveWindow =
      THREE.MathUtils.clamp((p - BREAKPOINTS.hero_end - 0.02) / 0.3, 0, 1) *
      THREE.MathUtils.clamp((BREAKPOINTS.final_end - 0.05 - p) / 0.3, 0, 1);
    sharedUniforms.uDissolveProgress.value = Math.max(0, dissolveWindow) * 0.45 + pulse * 0.12;
    sharedUniforms.uEdgeWidth.value = 0.12 + pulse * 0.1;

    // Particles / smoke ("Faden-, Staub- und Rauchpartikel") — a full,
    // billowing burst around the hero, thinning out as the garment
    // assembles, with an extra puff at every chapter breakpoint. Swirl is a
    // pure function of p (via sin/cos of p, not of clock time) — organic
    // motion that stays perfectly scroll-reversible.
    const overallArrived = THREE.MathUtils.clamp(
      (p - BREAKPOINTS.hero_end) / (BREAKPOINTS.final_end - BREAKPOINTS.hero_end),
      0,
      1
    );
    const remainingGlobal = 1 - overallArrived;
    const particleVisibility = (remainingGlobal * 0.9 + pulse * 0.5) * particleFadeCap;
    particleRig.material.opacity = THREE.MathUtils.clamp(particleVisibility, 0, 1) * 0.85;
    const posAttr = particleRig.points.geometry.getAttribute('position');
    for (let i = 0; i < particleRig.directions.length; i++) {
      const d = particleRig.directions[i];
      const swirl = particleRig.swirlSeeds[i];
      const spread = 1 + remainingGlobal * 1.6 + pulse * 0.5;
      const swirlAmount = remainingGlobal * 0.35;
      posAttr.array[i * 3] =
        particleRig.basePositions[i * 3] * spread + Math.sin(p * 9 + swirl) * swirlAmount;
      posAttr.array[i * 3 + 1] =
        particleRig.basePositions[i * 3 + 1] * spread +
        d.y * remainingGlobal * 0.4 +
        Math.cos(p * 7 + swirl) * swirlAmount;
      posAttr.array[i * 3 + 2] =
        particleRig.basePositions[i * 3 + 2] * spread + Math.sin(p * 5 + swirl * 1.3) * swirlAmount;
    }
    posAttr.needsUpdate = true;
  }

  return {
    setProgress,
    update,
    resize,
    whenLoaded,
    get loaded() {
      return loaded;
    },
  };
}
